import React, { useState, useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Link, useParams } from 'react-router-dom';
import { connect } from 'react-redux';
import { addUser, getUser, updateUser, updateDeposit, updateCans, getAllNotes, createNote, updateNote, deleteNote } from '../../actions/user';
import { getUserReferrals, getUserReferredBy } from '../../actions/referralLedger';
import { Row, Col, Table, Button, Card, Spinner, Badge, Form, Alert, Modal, Pagination } from "react-bootstrap";
import { MapPin, Navigation, Globe, Building2, Map } from 'lucide-react';

const initialState = {
    name: '',
    role:'',
    phone: '',
    email: '',
    suspend: false,
};

const depositInitialState = {depositAmount:0, depositBought:0, depositUsed:0, depositMaxLimit:0, freeDeposit:0, totalDeposit:0 };

const User = ( props ) => {
    const { id } = useParams();
    const [ formData, setFormData ] = useState(initialState);
    const [ depositData, setDepositData ] = useState(depositInitialState);
    const [ cansData, setCansData ] = useState({});
    const [ notes, setNotes ] = useState("");
    const [ editingNoteId, setEditingNoteId ] = useState(null);
    const [ currentNotePage, setCurrentNotePage ] = useState(1);
    const [ showDeleteModal, setShowDeleteModal ] = useState(false);
    const [ deleteNoteId, setDeleteNoteId ] = useState(null);
    const [ isSubmitting, setIsSubmitting ] = useState(false);
    const [ error, setError ] = useState('');
    const [ success, setSuccess ] = useState('');
    const [ showReferralModal, setShowReferralModal ] = useState(false);
    const [ referralType, setReferralType ] = useState('referrals'); // 'referrals' or 'referredBy'

    useEffect(() => {
        if( id !== 'new' ){
            if(!props.user ||  props.user._id !== id){
                // Get User details of a id
                props.getUser( id )
                props.getAllNotes(currentNotePage, id);
            }
            if (props.user) {
                setFormData({ ...props.user });
            }
            if (props.deposit) {
                setDepositData({ ...props.deposit });
            }
            if( props.cans ){
                let cans = {}
                // Handle new data structure with cans.delivery.brands
                if (props.cans.delivery && props.cans.delivery.brands) {
                    let deliveryObj = { name: "Delivery", brands: {} };
                    props.cans.delivery.brands.forEach((brand) => {
                        deliveryObj.brands[brand._id] = {
                            brand: brand.brand, 
                            product: brand.product, 
                            totalCans: brand.totalCans, 
                            difference: 0,
                            orders: brand.orders || []
                        };
                    });
                    cans['delivery'] = deliveryObj;
                }
                // Fallback to old vendingMachines structure if delivery doesn't exist
                else if (props.cans.vendingMachines) {
                    props.cans.vendingMachines.forEach((obj) => {
                        let brandObj = { name: obj.vendingMachine.name, brands:{} };
                        obj.brands.forEach((brand) => {
                            brandObj.brands[brand._id] = {brand: brand.brand, product: brand.product, totalCans: brand.totalCans, difference: 0};
                        });
                        cans[obj._id] = brandObj;
                    });
                }
                setCansData(cans);
            }
        }        
    }, [ props.user, props.cans, props.deposit, id, currentNotePage ]);

    // Clear messages after 5 seconds
    useEffect(() => {
        if (error || success) {
            const timer = setTimeout(() => {
                setError('');
                setSuccess('');
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [error, success]);

    const {
        name,
        role,
        phone,
        email,
        suspend,
    } = formData;

    const { depositAmount, depositBought, depositUsed, depositMaxLimit, freeDeposit, totalDeposit } = depositData;

    const isNewUser  =  (id==='new')? true : false ;
    
    const onChange = (e, type) =>{
        if(type === "deposit"){
            setDepositData({ ...depositData, [e.target.name]: e.target.value === "" ? 0 : Number(e.target.value) });
        }else{
            setFormData({ ...formData, [e.target.name]: e.target.value });
        }
    }

    const onChangeNote = (e) => {
        setNotes(e.target.value);
    }

    const onCanChange = (e, brandId, vendingMachineId) => {
        let cans = { ...cansData };
        
        // Handle both delivery and vendingMachine structures
        if (vendingMachineId === 'delivery' && cans[vendingMachineId]) {
            // New delivery structure
            cans[vendingMachineId]["brands"][brandId].totalCans = Number(e.target.value);
        } else if (cans[vendingMachineId] && cans[vendingMachineId]["brands"]) {
            // Old vendingMachine structure
            cans[vendingMachineId]["brands"][brandId].totalCans = Number(e.target.value);
        }
        
        setCansData(cans);
    }

    const updateCanData = async (brandId, vendingMachineId, cans, vmArrayIndex, brandIndex) => {
        try {
            setIsSubmitting(true);
            
            // Use new cans.delivery structure instead of vendingMachines
            let prevCanCount, brand, canData;
            
            if (props.cans.delivery && props.cans.delivery.brands) {
                // New structure: cans.delivery.brands
                prevCanCount = props.cans.delivery.brands[vmArrayIndex].totalCans;
                let differences = cans - prevCanCount;
                brand = props.cans.delivery.brands[vmArrayIndex].product;
                canData = { 
                    totalCans: props.cans.totalCans + differences, 
                    cans: cans,
                    brand: brand,
                    delivery: true // Flag to indicate this is delivery cans
                };
            } else if (props.cans.vendingMachines) {
                // Fallback to old structure for backward compatibility
                prevCanCount = props.cans.vendingMachines[vmArrayIndex]["brands"][brandIndex].totalCans;
                let differences = cans - prevCanCount;
                brand = props.cans.vendingMachines[vmArrayIndex].brands[brandIndex].product;
                const vendingMachine = props.cans.vendingMachines[vmArrayIndex].vendingMachine._id;
                canData = { 
                    totalCans: props.cans.totalCans + differences, 
                    cans: cans,
                    vendingMachine: vendingMachine, 
                    brand: brand 
                };
            } else {
                throw new Error('No valid cans structure found');
            }
            await props.updateCans(props.user._id, canData);
            setSuccess('Cans updated successfully!');
        } catch (err) {
            setError('Failed to update cans. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    }
    
    const onSubmit = async (e) => {
        e.preventDefault();
        try {
            setIsSubmitting(true);
            setError('');
            if(isNewUser){
                await props.addUser(formData);
                setSuccess('User added successfully!');
            }else{
                // Only allow role and suspend status to be updated
                const updateData = { role: formData.role, suspend: formData.suspend };
                await props.updateUser(props.user._id, updateData);
                setSuccess('User updated successfully!');
            }
        } catch (err) {
            setError(isNewUser ? 'Failed to add user. Please try again.' : 'Failed to update user. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const updateDeposit = async (e) => {
        e.preventDefault();
        try {
            setIsSubmitting(true);
            setError('');
            await props.updateDeposit(props.deposit._id, props.user._id, depositData);
            setSuccess('Deposit updated successfully!');
        } catch (err) {
            setError('Failed to update deposit. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    }

    const handleCreateNote = async () => {
        if (!notes.trim()) {
            setError('Please enter a note before saving.');
            return;
        }
        try {
            setIsSubmitting(true);
            setError('');
            await props.createNote({
                user: props.user._id,
                note: notes
            });
            setNotes('');
            setCurrentNotePage(1); // Reset to first page after creating
            // Refresh notes after creating
            props.getAllNotes(1, props.user._id);
            setSuccess('Note created successfully!');
        } catch (err) {
            setError('Failed to create note. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    }

    const handleUpdateNote = async (noteId) => {
        if (!notes.trim()) {
            setError('Please enter a note before updating.');
            return;
        }
        try {
            setIsSubmitting(true);
            setError('');
            await props.updateNote(noteId, {
                user: props.user._id,
                note: notes
            });
            setNotes('');
            setEditingNoteId(null);
            // Refresh current page after updating
            props.getAllNotes(currentNotePage, props.user._id);
            setSuccess('Note updated successfully!');
        } catch (err) {
            setError('Failed to update note. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    }

    const handleDeleteNote = (noteId) => {
        setDeleteNoteId(noteId);
        setShowDeleteModal(true);
    }

    const confirmDeleteNote = async () => {
        try {
            setIsSubmitting(true);
            setError('');
            await props.deleteNote(deleteNoteId);
            setShowDeleteModal(false);
            setDeleteNoteId(null);
            // Refresh current page after deleting
            props.getAllNotes(currentNotePage, props.user._id);
            setSuccess('Note deleted successfully!');
        } catch (err) {
            setError('Failed to delete note. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    }

    const cancelDeleteNote = () => {
        setShowDeleteModal(false);
        setDeleteNoteId(null);
    }

    const handleEditNote = (note) => {
        setNotes(note.note);
        setEditingNoteId(note._id);
    }

    const handleCancelEdit = () => {
        setNotes('');
        setEditingNoteId(null);
    }

    const handlePageChange = (page) => {
        setCurrentNotePage(page);
    }

    const handleGetUserReferrals = async () => {
        try {
            setIsSubmitting(true);
            setError('');
            await props.getUserReferrals(props.user._id);
            setReferralType('referrals');
            setShowReferralModal(true);
            setSuccess('User referrals loaded successfully!');
        } catch (err) {
            setError('Failed to fetch user referrals. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleGetUserReferredBy = async () => {
        try {
            setIsSubmitting(true);
            setError('');
            await props.getUserReferredBy(props.user._id);
            setReferralType('referredBy');
            setShowReferralModal(true);
            setSuccess('User referred by data loaded successfully!');
        } catch (err) {
            setError('Failed to fetch user referred by data. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const getStatusBadge = (status) => {
        const statusConfig = {
            pending: { variant: 'warning', text: 'Pending' },
            completed: { variant: 'success', text: 'Completed' },
            cancelled: { variant: 'danger', text: 'Cancelled' },
            expired: { variant: 'secondary', text: 'Expired' }
        };
        
        const config = statusConfig[status] || { variant: 'secondary', text: status };
        return <Badge bg={config.variant}>{config.text}</Badge>;
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getUserNotes = () => {
        // API now only returns notes for the specific user, so return all notes directly
        return props.notes?.notes || [];
    }

    const renderPagination = () => {
        const { totalPages, currentPage } = props.notes || {};
        if (!totalPages || totalPages <= 1) return null;

        const items = [];
        const maxVisiblePages = 5;
        const startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

        // First page
        if (startPage > 1) {
            items.push(
                <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
                    1
                </Pagination.Item>
            );
            if (startPage > 2) {
                items.push(<Pagination.Ellipsis key="ellipsis-start" />);
            }
        }

        // Page numbers
        for (let number = startPage; number <= endPage; number++) {
            items.push(
                <Pagination.Item
                    key={number}
                    active={number === currentPage}
                    onClick={() => handlePageChange(number)}
                >
                    {number}
                </Pagination.Item>
            );
        }

        // Last page
        if (endPage < totalPages) {
            if (endPage < totalPages - 1) {
                items.push(<Pagination.Ellipsis key="ellipsis-end" />);
            }
            items.push(
                <Pagination.Item key={totalPages} onClick={() => handlePageChange(totalPages)}>
                    {totalPages}
                </Pagination.Item>
            );
        }

        return (
            <div className="d-flex justify-content-center mt-4">
                <Pagination>
                    <Pagination.Prev
                        disabled={currentPage === 1}
                        onClick={() => handlePageChange(currentPage - 1)}
                    />
                    {items}
                    <Pagination.Next
                        disabled={currentPage === totalPages}
                        onClick={() => handlePageChange(currentPage + 1)}
                    />
                </Pagination>
            </div>
        );
    }

    const renderUserData = () => {
        return (
            <Card className="h-100 shadow-sm user-card">
                <Card.Header className="bg-primary text-white">
                    <h5 className="mb-0">
                        <i className="fas fa-user me-2"></i>
                        User Information
                    </h5>
                </Card.Header>
                <Card.Body>
                    <Form onSubmit={onSubmit}>
                        <Form.Group className="mb-3">
                            <Form.Label>Name *</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Enter full name"
                                name="name"
                                value={name || ""}
                                onChange={onChange}
                                required
                                disabled={!isNewUser || isSubmitting}
                                readOnly={!isNewUser}
                            />
                            {!isNewUser && (
                                <Form.Text className="text-muted">
                                    <i className="fas fa-lock me-1"></i>
                                    Name cannot be modified
                                </Form.Text>
                            )}
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Phone</Form.Label>
                            <Form.Control
                                type="tel"
                                placeholder="Enter phone number"
                                name="phone"
                                value={phone || ""}
                                onChange={onChange}
                                disabled={!isNewUser || isSubmitting}
                                readOnly={!isNewUser}
                            />
                            {!isNewUser && (
                                <Form.Text className="text-muted">
                                    <i className="fas fa-lock me-1"></i>
                                    Phone cannot be modified
                                </Form.Text>
                            )}
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Email</Form.Label>
                            <Form.Control
                                type="email"
                                placeholder="Enter email address"
                                name="email"
                                value={email || ""}
                                onChange={onChange}
                                disabled={!isNewUser || isSubmitting}
                                readOnly={!isNewUser}
                            />
                            {!isNewUser && (
                                <Form.Text className="text-muted">
                                    <i className="fas fa-lock me-1"></i>
                                    Email cannot be modified
                                </Form.Text>
                            )}
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Role</Form.Label>
                            <Form.Select
                                value={role || ""}
                                name="role"
                                onChange={onChange}
                                disabled={isSubmitting}
                                required
                            >
                                <option value="">Select Role</option>
                                <option value="admin">Admin</option>
                                <option value="user">User</option>
                                <option value="manager">Manager</option>
                            </Form.Select>
                        </Form.Group>

                        <Form.Group className="mb-4">
                            <Form.Label>Account Status</Form.Label>
                            <Form.Select
                                value={suspend}
                                name="suspend"
                                onChange={onChange}
                                disabled={isSubmitting}
                            >
                                <option value="false">Active</option>
                                <option value="true">Suspended</option>
                            </Form.Select>
                        </Form.Group>

                        <div className="d-grid">
                            <Button 
                                type="submit" 
                                variant="primary" 
                                disabled={isSubmitting}
                                size="lg"
                            >
                                {isSubmitting ? (
                                    <>
                                        <Spinner size="sm" className="me-2" />
                                        {isNewUser ? 'Adding...' : 'Updating...'}
                                    </>
                                ) : (
                                    isNewUser ? 'Add User' : 'Update User'
                                )}
                            </Button>
                        </div>
                    </Form>
                </Card.Body>
            </Card>
        );
    }

    const renderReferralInfo = () => {
        const referral = props.referral;

        return (
            <Card className="mb-4 shadow-sm user-card">
                <Card.Header className="bg-info text-white">
                    <h5 className="mb-0">
                        <i className="fas fa-handshake me-2"></i>
                        Referral Information
                    </h5>
                </Card.Header>
                <Card.Body>
                    {referral && referral.referralId ? (
                        <>
                            <Row>
                                <Col md={6} className="mb-3">
                                    <div className="d-flex align-items-center">
                                        <i className="fas fa-id-card me-2 text-primary"></i>
                                        <div>
                                            <small className="text-muted d-block">Referral ID</small>
                                            <Badge bg="primary" className="fs-6">
                                                {referral.referralId || 'N/A'}
                                            </Badge>
                                        </div>
                                    </div>
                                </Col>
                                <Col md={6} className="mb-3">
                                    <div className="d-flex align-items-center">
                                        <i className="fas fa-gift me-2 text-success"></i>
                                        <div>
                                            <small className="text-muted d-block">Total Cans</small>
                                            <Badge bg="success" className="fs-6">
                                                {referral.total || 0} cans
                                            </Badge>
                                        </div>
                                    </div>
                                </Col>
                                <Col md={6} className="mb-3">
                                    <div className="d-flex align-items-center">
                                        <i className="fas fa-coins me-2 text-warning"></i>
                                        <div>
                                            <small className="text-muted d-block">Current Cans</small>
                                            <Badge bg="warning" className="fs-6">
                                                {referral.cans || 0} cans
                                            </Badge>
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                            <div className="mt-3">
                                <div className="d-flex justify-content-between align-items-center">
                                    <span className="text-muted">Referral Progress</span>
                                    <span className="fw-bold">
                                        {referral.total > 0 ? Math.round((referral.cans / referral.total) * 100) : 0}%
                                    </span>
                                </div>
                                <div className="progress mt-2" style={{height: '8px'}}>
                                    <div 
                                        className="progress-bar bg-gradient" 
                                        style={{
                                            width: `${referral.total > 0 ? Math.round((referral.cans / referral.total) * 100) : 0}%`,
                                            background: 'linear-gradient(45deg, #28a745, #20c997)'
                                        }}
                                    ></div>
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="text-center py-4">
                            <i className="fas fa-handshake fa-3x text-muted mb-3"></i>
                            <p className="text-muted">No referral information available for this user.</p>
                            <p className="text-muted">Referral data will appear here once the user participates in the referral program.</p>
                        </div>
                    )}
                </Card.Body>
            </Card>
        );
    };

    const renderNewUserInfo = () => {
        const newUser = props.user?.newUser;
        if (!newUser) return null;

        return (
            <Card className="mb-4 shadow-sm user-card">
                <Card.Header className="bg-success text-white">
                    <h5 className="mb-0">
                        <i className="fas fa-user-check me-2"></i>
                        New User Status
                    </h5>
                </Card.Header>
                <Card.Body>
                    <Row>
                        <Col md={6} className="mb-3">
                            <div className="d-flex align-items-center">
                                <i className="fas fa-shopping-cart me-2 text-primary"></i>
                                <div>
                                    <small className="text-muted d-block">Has Purchased</small>
                                    <Badge bg={newUser.hasPurchased ? 'success' : 'secondary'} className="fs-6">
                                        {newUser.hasPurchased ? 'Yes' : 'No'}
                                    </Badge>
                                </div>
                            </div>
                        </Col>
                        <Col md={6} className="mb-3">
                            <div className="d-flex align-items-center">
                                <i className="fas fa-truck me-2 text-info"></i>
                                <div>
                                    <small className="text-muted d-block">Has Completed Delivery</small>
                                    <Badge bg={newUser.hasCompletedDelivery ? 'success' : 'secondary'} className="fs-6">
                                        {newUser.hasCompletedDelivery ? 'Yes' : 'No'}
                                    </Badge>
                                </div>
                            </div>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>
        );
    }

    const renderAddressInfo = () => {
        const addresses = props.user?.address;
        if (!addresses || !Array.isArray(addresses) || addresses.length === 0) {
            return (
                <Card className="mb-4 shadow-sm user-card">
                    <Card.Header className="bg-info text-white">
                        <h5 className="mb-0">
                            <MapPin size={20} className="me-2" />
                            Address Information
                        </h5>
                    </Card.Header>
                    <Card.Body>
                        <div className="text-center py-4">
                            <MapPin size={48} className="text-muted mb-3" />
                            <p className="text-muted">No address information available for this user.</p>
                        </div>
                    </Card.Body>
                </Card>
            );
        }

        return (
            <Card className="mb-4 shadow-sm user-card">
                <Card.Header className="bg-info text-white">
                    <h5 className="mb-0">
                        <MapPin size={20} className="me-2" />
                        Address Information ({addresses.length} {addresses.length === 1 ? 'address' : 'addresses'})
                    </h5>
                </Card.Header>
                <Card.Body>
                    <Row>
                        {addresses.map((address, index) => (
                            <Col key={address._id || index} md={6} className="mb-4">
                                <Card className="border">
                                    <Card.Header className="bg-light">
                                        <div className="d-flex justify-content-between align-items-center">
                                            <h6 className="mb-0">
                                                <Navigation size={16} className="me-2 text-primary" />
                                                Address #{index + 1}
                                            </h6>
                                            <Badge bg="primary" className="fs-6">
                                                {address._id?.slice(-6)}
                                            </Badge>
                                        </div>
                                    </Card.Header>
                                    <Card.Body>
                                        <div className="mb-3">
                                            <div className="d-flex align-items-start">
                                                <MapPin size={16} className="me-2 text-muted mt-1" />
                                                <div className="flex-grow-1">
                                                    <small className="text-muted d-block">Full Address</small>
                                                    <div className="fw-bold text-break">
                                                        {address.fullAddress || 'No address provided'}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {address.location && address.location.coordinates && (
                                            <div className="mb-3">
                                                <div className="d-flex align-items-center">
                                                    <Globe size={16} className="me-2 text-muted" />
                                                    <div className="flex-grow-1">
                                                        <small className="text-muted d-block">GPS Coordinates</small>
                                                        <div className="fw-bold">
                                                            {address.location.coordinates[0]}, {address.location.coordinates[1]}
                                                        </div>
                                                        <small className="text-muted">
                                                            Longitude: {address.location.coordinates[0]} |
                                                            Latitude: {address.location.coordinates[1]}
                                                        </small>
                                                        <div className="mt-2">
                                                            <Button
                                                                variant="outline-primary"
                                                                size="sm"
                                                                onClick={() => {
                                                                    const googleMapsUrl = process.env.REACT_APP_GOOGLE_MAPS_URL || 'https://www.google.com/maps';
                                                                    const latitude = address.location.coordinates[0];
                                                                    const longitude = address.location.coordinates[1];
                                                                    const mapsUrl = `${googleMapsUrl}?q=${latitude},${longitude}`;
                                                                    window.open(mapsUrl, '_blank', 'noopener,noreferrer');
                                                                }}
                                                                className="d-flex align-items-center"
                                                            >
                                                                <MapPin size={14} className="me-1" />
                                                                View on Google Maps
                                                            </Button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {address.location && address.location.type && (
                                            <div className="mb-3">
                                                <div className="d-flex align-items-center">
                                                    <Navigation size={16} className="me-2 text-muted" />
                                                    <div>
                                                        <small className="text-muted d-block">Location Type</small>
                                                        <Badge bg="info" className="fs-6">
                                                            {address.location.type}
                                                        </Badge>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {address.sectorId && (
                                            <div className="mb-3">
                                                <div className="d-flex align-items-center">
                                                    <Building2 size={16} className="me-2 text-muted" />
                                                    <div>
                                                        <small className="text-muted d-block">Sector</small>
                                                        <div className="fw-bold">
                                                            {address.sectorId.name || 'N/A'}
                                                        </div>
                                                        {address.sectorId._id && (
                                                            <small className="text-muted">
                                                                ID: {address.sectorId._id.slice(-6)}
                                                            </small>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {address.serviceableArea && (
                                            <div className="mb-0">
                                                <div className="d-flex align-items-center">
                                                    <Map size={16} className="me-2 text-muted" />
                                                    <div>
                                                        <small className="text-muted d-block">Serviceable Area</small>
                                                        <div className="fw-bold">
                                                            {address.serviceableArea.name || 'N/A'}
                                                        </div>
                                                        {address.serviceableArea._id && (
                                                            <small className="text-muted">
                                                                ID: {address.serviceableArea._id.slice(-6)}
                                                            </small>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </Card.Body>
                                </Card>
                            </Col>
                        ))}
                    </Row>
                </Card.Body>
            </Card>
        );
    }

    const renderDepositData = () => {
        return (
            <Card className="h-100 shadow-sm user-card">
                <Card.Header className="bg-warning text-dark">
                    <h5 className="mb-0">
                        <i className="fas fa-wallet me-2"></i>
                        Deposit Management
                    </h5>
                </Card.Header>
                <Card.Body>
                    <Form onSubmit={updateDeposit}>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Deposit Amount (₹)</Form.Label>
                                    <Form.Control
                                        type="number"
                                        placeholder="0"
                                        name="depositAmount"
                                        value={depositAmount || 0}
                                        onChange={(e) => onChange(e, "deposit")}
                                        min="0"
                                        step="0.01"
                                        disabled={isSubmitting}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Max Limit</Form.Label>
                                    <Form.Control
                                        type="number"
                                        placeholder="0"
                                        name="depositMaxLimit"
                                        value={depositMaxLimit || 0}
                                        onChange={(e) => onChange(e, "deposit")}
                                        min="0"
                                        step="0.01"
                                        disabled={isSubmitting}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Deposit Bought</Form.Label>
                                    <Form.Control
                                        type="number"
                                        placeholder="0"
                                        name="depositBought"
                                        value={depositBought || 0}
                                        onChange={(e) => onChange(e, "deposit")}
                                        min="0"
                                        disabled={isSubmitting}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Deposit Used</Form.Label>
                                    <Form.Control
                                        type="number"
                                        placeholder="0"
                                        name="depositUsed"
                                        value={depositUsed || 0}
                                        onChange={(e) => onChange(e, "deposit")}
                                        min="0"
                                        disabled={isSubmitting}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-4">
                                    <Form.Label>Free Deposit</Form.Label>
                                    <Form.Control
                                        type="number"
                                        placeholder="0"
                                        name="freeDeposit"
                                        value={freeDeposit || 0}
                                        onChange={(e) => onChange(e, "deposit")}
                                        min="0"
                                        step="0.01"
                                        disabled={isSubmitting}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-4">
                                    <Form.Label>Total Deposit</Form.Label>
                                    <Form.Control
                                        type="number"
                                        placeholder="0"
                                        name="totalDeposit"
                                        value={totalDeposit || 0}
                                        onChange={(e) => onChange(e, "deposit")}
                                        min="0"
                                        step="0.01"
                                        disabled={isSubmitting}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>                        

                        <div className="d-grid">
                            <Button 
                                type="submit" 
                                variant="warning"
                                disabled={isSubmitting || isNewUser}
                                size="lg"
                            >
                                {isSubmitting ? (
                                    <>
                                        <Spinner size="sm" className="me-2" />
                                        Updating...
                                    </>
                                ) : (
                                    'Update Deposit'
                                )}
                            </Button>
                        </div>
                    </Form>
                </Card.Body>
            </Card>
        );
    }

    const renderTableData = (brands, vendingMachineId, vmArrayIndex) => {
        return (
            Object.keys(brands).map((value, index) => {
                return (
                    <tr key={value}>
                        <td className="fw-bold">{brands[value].brand}</td>
                        <td>
                            <Form.Control
                                type="number"
                                name="totalCans"
                                value={cansData[vendingMachineId]["brands"][value].totalCans}
                                onChange={(e) => onCanChange(e, value, vendingMachineId)}
                                min="0"
                                disabled={isSubmitting}
                                className="text-center"
                            />
                        </td>
                        <td>
                            <Button 
                                onClick={() => updateCanData(value, vendingMachineId, cansData[vendingMachineId]["brands"][value].totalCans, vmArrayIndex, index)} 
                                variant="primary"
                                size="sm"
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? <Spinner size="sm" /> : 'Update'}
                            </Button>
                        </td>
                    </tr>
                );
            })
        )
    }

    const renderCanData = () => {
        return (
            <div className="row">
                {Object.keys(cansData).map((value, index) => (
                    <div key={index} className="col-md-6 mb-4">
                        <Card className="shadow-sm user-card">
                            <Card.Header className="bg-info text-white">
                                <h6 className="mb-0">
                                    <i className="fas fa-cog me-2"></i>
                                    {cansData[value].name}
                                </h6>
                            </Card.Header>
                            <Card.Body className="p-0">
                                <Table striped hover responsive className="mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Brand</th>
                                            <th className="text-center">Cans</th>
                                            <th className="text-center">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {renderTableData(cansData[value].brands, value, index)}
                                    </tbody>
                                </Table>
                            </Card.Body>
                        </Card>
                    </div>
                ))}
            </div>
        );
    }

    const renderCanReturnsInfo = () => {
        const canReturns = props.canReturns || [];
        const requestedCans = canReturns.reduce((sum, cr) => sum + (cr.numberOfCans || 0), 0);
        const refundedCans = canReturns
            .filter(cr => cr.status === 'refunded')
            .reduce((sum, cr) => sum + (cr.numberOfCans || 0), 0);
        const pendingCans = requestedCans - refundedCans;

        return (
            <Card className="mb-5 shadow-sm user-card">
                <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">
                        <i className="fas fa-undo-alt me-2"></i>
                        Can Returns
                    </h5>
                    {canReturns.length > 0 && props.user && (
                        <Link
                            to={`/inventory/v1.2/can-returns?user=${props.user._id}`}
                            className="btn btn-light btn-sm"
                        >
                            View Details
                        </Link>
                    )}
                </Card.Header>
                <Card.Body>
                    {canReturns.length > 0 ? (
                        <Row>
                            <Col md={4} className="mb-3">
                                <div className="d-flex align-items-center">
                                    <i className="fas fa-boxes me-2 text-primary"></i>
                                    <div>
                                        <small className="text-muted d-block">Requested for Return</small>
                                        <Badge bg="primary" className="fs-6">{requestedCans} cans</Badge>
                                    </div>
                                </div>
                            </Col>
                            <Col md={4} className="mb-3">
                                <div className="d-flex align-items-center">
                                    <i className="fas fa-check-circle me-2 text-success"></i>
                                    <div>
                                        <small className="text-muted d-block">Refunded</small>
                                        <Badge bg="success" className="fs-6">{refundedCans} cans</Badge>
                                    </div>
                                </div>
                            </Col>
                            <Col md={4} className="mb-3">
                                <div className="d-flex align-items-center">
                                    <i className="fas fa-hourglass-half me-2 text-warning"></i>
                                    <div>
                                        <small className="text-muted d-block">Pending / In Progress</small>
                                        <Badge bg="warning" className="fs-6">{pendingCans} cans</Badge>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    ) : (
                        <div className="text-center py-4">
                            <i className="fas fa-undo-alt fa-3x text-muted mb-3"></i>
                            <p className="text-muted">No can return requests for this user.</p>
                        </div>
                    )}
                </Card.Body>
            </Card>
        );
    };

    if (props.loading) {
        return (
            <div className="d-flex justify-content-center align-items-center loading-container" style={{minHeight: '400px'}}>
                <div className="text-center">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-3 text-muted">Loading user details...</p>
                </div>
            </div>
        );
    }

    return (
        <Fragment>
            <div className="container-fluid py-4" style={{background: '#f8fafc', minHeight: '100vh'}}>
                {/* Header */}
                <div className="mb-4">
                    <Link 
                        to="/inventory/v1.2/users"
                        className="btn btn-outline-primary mb-3"
                    >
                        <i className="fas fa-arrow-left me-2"></i>
                        Back to Users
                    </Link>
                    <div>
                        <h2 className="text-primary mb-1">
                            {isNewUser ? 'Add New User' : `User: ${name || 'Loading...'}`}
                        </h2>
                        <p className="text-muted mb-0">
                            {isNewUser ? 'Create a new user account' : 'Manage user information and settings'}
                        </p>
                    </div>
                </div>

                {/* Alert Messages */}
                {error && (
                    <Alert variant="danger" dismissible onClose={() => setError('')}>
                        <i className="fas fa-exclamation-circle me-2"></i>
                        {error}
                    </Alert>
                )}
                {success && (
                    <Alert variant="success" dismissible onClose={() => setSuccess('')}>
                        <i className="fas fa-check-circle me-2"></i>
                        {success}
                    </Alert>
                )}

                {/* Main Form Section */}
                <Row className="mb-5">
                    <Col lg={6} className="mb-4">
                        {renderUserData()}
                    </Col>
                    <Col lg={6} className="mb-4">
                        {renderDepositData()}
                    </Col>
                </Row>

                {/* New User Status */}
                {!isNewUser && renderNewUserInfo()}

                {/* Address Information */}
                {!isNewUser && renderAddressInfo()}

                {/* Referral Information */}
                {renderReferralInfo()}

                {!isNewUser && (
                    <>
                        {/* Quick Actions */}
                        <Card className="mb-5 shadow-sm user-card">
                            <Card.Header className="bg-primary text-white">
                                <h5 className="mb-0">
                                    <i className="fas fa-bolt me-2"></i>
                                    Quick Actions
                                </h5>
                            </Card.Header>
                            <Card.Body className="quick-actions">
                                <div className="row text-center">
                                    <div className="col-md-4 mb-3">
                                        <Button
                                            as={Link}
                                            to={`/inventory/v1.2/Orders?user=${props.user ? props.user._id: null}`}
                                            target="_blank"
                                            variant="outline-primary"
                                            className="w-100 py-3 quick-action-btn"
                                        >
                                            <i className="fas fa-shopping-cart fa-2x d-block mb-2"></i>
                                            View Orders
                                        </Button>
                                    </div>
                                    <div className="col-md-4 mb-3">
                                        <Button
                                            as={Link}
                                            to={`/inventory/v1.2/delivery-transactions?user=${props.user ? props.user._id: null}`}
                                            target="_blank"
                                            variant="outline-success"
                                            className="w-100 py-3 quick-action-btn"
                                        >
                                            <i className="fas fa-credit-card fa-2x d-block mb-2"></i>
                                            View Transactions
                                        </Button>
                                    </div>
                                    <div className="col-md-4 mb-3">
                                        <Button
                                            as={Link}
                                            to={`/inventory/v1.2/ledger?user=${props.user ? props.user._id: null}`}
                                            target="_blank"
                                            variant="outline-info"
                                            className="w-100 py-3 quick-action-btn"
                                        >
                                            <i className="fas fa-book fa-2x d-block mb-2"></i>
                                            View Can Ledger
                                        </Button>
                                    </div>
                                    <div className="col-md-4 mb-3">
                                        <Button
                                            as={Link}
                                            to={`/inventory/v1.2/deposit-ledger?user=${props.user ? props.user._id: null}`}
                                            target="_blank"
                                            variant="outline-warning"
                                            className="w-100 py-3 quick-action-btn"
                                        >
                                            <i className="fas fa-piggy-bank fa-2x d-block mb-2"></i>
                                            View Deposit Ledger
                                        </Button>
                                    </div>
                                    <div className="col-md-4 mb-3">
                                        <Button
                                            onClick={handleGetUserReferrals}
                                            variant="outline-info"
                                            className="w-100 py-3 quick-action-btn"
                                            disabled={isSubmitting}
                                        >
                                            <i className="fas fa-handshake fa-2x d-block mb-2"></i>
                                            View Referrals Made
                                        </Button>
                                    </div>
                                    <div className="col-md-4 mb-3">
                                        <Button
                                            onClick={handleGetUserReferredBy}
                                            variant="outline-success"
                                            className="w-100 py-3 quick-action-btn"
                                            disabled={isSubmitting}
                                        >
                                            <i className="fas fa-user-plus fa-2x d-block mb-2"></i>
                                            View Referred By
                                        </Button>
                                    </div>
                                    <div className="col-md-4 mb-3">
                                        <Button
                                            as={Link}
                                            to={`/inventory/v1.2/whatsapp/timeline/${props.user ? props.user._id: null}`}
                                            target="_blank"
                                            variant="outline-success"
                                            className="w-100 py-3 quick-action-btn"
                                        >
                                            <i className="fab fa-whatsapp fa-2x d-block mb-2"></i>
                                            View WhatsApp Timeline
                                        </Button>
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>

                        {/* Cans Management */}
                        <Card className="mb-5 shadow-sm user-card">
                            <Card.Header className="bg-info text-white d-flex justify-content-between align-items-center">
                                <h5 className="mb-0">
                                    <i className="fas fa-cogs me-2"></i>
                                    Cans Management
                                </h5>
                                <div className="d-flex gap-2">
                                    <Badge bg="light" text="dark" className="fs-6">
                                        Total: {props.cans ? props.cans.totalCans : 0} cans
                                    </Badge>
                                    <Badge bg="light" text="dark" className="fs-6">
                                        Returned: {props.cans ? props.cans.returnedCans || 0 : 0} cans
                                    </Badge>
                                </div>
                            </Card.Header>
                            <Card.Body>
                                {Object.keys(cansData).length > 0 ? (
                                    renderCanData()
                                ) : (
                                    <div className="text-center py-4">
                                        <i className="fas fa-cogs fa-3x text-muted mb-3"></i>
                                        <p className="text-muted">No cans data available for this user.</p>
                                    </div>
                                )}
                            </Card.Body>
                        </Card>

                        {/* Can Return Requests */}
                        {renderCanReturnsInfo()}

                        {/* Notes Management */}
                        <Card className="shadow-sm user-card">
                            <Card.Header className="bg-secondary text-white">
                                <h5 className="mb-0">
                                    <i className="fas fa-sticky-note me-2"></i>
                                    Notes Management
                                </h5>
                            </Card.Header>
                            <Card.Body>
                                {/* Create/Edit Note Form */}
                                <Form.Group className="mb-3">
                                    <Form.Label>
                                        {editingNoteId ? 'Edit Note' : 'Add New Note'}
                                    </Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={4}
                                        placeholder="Enter your note here..."
                                        value={notes}
                                        onChange={onChangeNote}
                                        disabled={isSubmitting}
                                    />
                                </Form.Group>

                                <div className="d-flex gap-2 mb-4">
                                    <Button 
                                        onClick={editingNoteId ? () => handleUpdateNote(editingNoteId) : handleCreateNote}
                                        variant={editingNoteId ? "warning" : "secondary"}
                                        disabled={isSubmitting || !notes.trim()}
                                        className="flex-grow-1"
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <Spinner size="sm" className="me-2" />
                                                {editingNoteId ? 'Updating...' : 'Creating...'}
                                            </>
                                        ) : (
                                            <>
                                                <i className={`fas ${editingNoteId ? 'fa-edit' : 'fa-plus'} me-2`}></i>
                                                {editingNoteId ? 'Update Note' : 'Create Note'}
                                            </>
                                        )}
                                    </Button>
                                    {editingNoteId && (
                                        <Button 
                                            onClick={handleCancelEdit}
                                            variant="outline-secondary"
                                            disabled={isSubmitting}
                                        >
                                            <i className="fas fa-times me-2"></i>
                                            Cancel
                                        </Button>
                                    )}
                                </div>

                                {/* Notes List */}
                                <div>
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <h6 className="text-muted mb-0">
                                            <i className="fas fa-list me-2"></i>
                                            User Notes ({props.notes?.totalNotes || 0} total)
                                        </h6>
                                        {props.notes?.totalPages > 1 && (
                                            <small className="text-muted">
                                                Page {props.notes?.currentPage || 1} of {props.notes?.totalPages || 1}
                                            </small>
                                        )}
                                    </div>

                                    {getUserNotes().length > 0 ? (
                                        <div className="notes-list">
                                            {getUserNotes().map((note, index) => (
                                                <Card key={note._id} className="mb-3 border">
                                                    <Card.Body className="py-3">
                                                        <div className="d-flex justify-content-between align-items-start">
                                                            <div className="flex-grow-1">
                                                                <p className="mb-2 text-break">{note.note}</p>
                                                                <small className="text-muted">
                                                                    <i className="fas fa-clock me-1"></i>
                                                                    {new Date(note.createdAt || note.updatedAt).toLocaleString()}
                                                                </small>
                                                            </div>
                                                            <div className="d-flex gap-2 ms-3">
                                                                <Button
                                                                    size="sm"
                                                                    variant="outline-primary"
                                                                    onClick={() => handleEditNote(note)}
                                                                    disabled={isSubmitting}
                                                                    title="Edit Note"
                                                                >
                                                                    <i className="fas fa-edit"></i>
                                                                </Button>
                                                                <Button
                                                                    size="sm"
                                                                    variant="outline-danger"
                                                                    onClick={() => handleDeleteNote(note._id)}
                                                                    disabled={isSubmitting}
                                                                    title="Delete Note"
                                                                >
                                                                    <i className="fas fa-trash"></i>
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    </Card.Body>
                                                </Card>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="text-center py-4">
                                            <i className="fas fa-sticky-note fa-3x text-muted mb-3"></i>
                                            <p className="text-muted">No notes found for this user.</p>
                                            <p className="text-muted">Create your first note using the form above.</p>
                                        </div>
                                    )}

                                    {/* Always show pagination if there are multiple pages */}
                                    {renderPagination()}
                                </div>
                            </Card.Body>
                        </Card>
                    </>
                )}
            </div>

            {/* Delete Confirmation Modal */}
            <Modal show={showDeleteModal} onHide={cancelDeleteNote} centered>
                <Modal.Header closeButton className="bg-danger text-white">
                    <Modal.Title>
                        <i className="fas fa-exclamation-triangle me-2"></i>
                        Confirm Delete
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="text-center py-4">
                    <div className="mb-3">
                        <i className="fas fa-trash-alt fa-3x text-danger mb-3"></i>
                    </div>
                    <h5 className="mb-3">Are you sure you want to delete this note?</h5>
                    <p className="text-muted mb-0">
                        This action cannot be undone. The note will be permanently removed from the system.
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button 
                        variant="outline-secondary" 
                        onClick={cancelDeleteNote}
                        disabled={isSubmitting}
                    >
                        <i className="fas fa-times me-2"></i>
                        Cancel
                    </Button>
                    <Button 
                        variant="danger" 
                        onClick={confirmDeleteNote}
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? (
                            <>
                                <Spinner size="sm" className="me-2" />
                                Deleting...
                            </>
                        ) : (
                            <>
                                <i className="fas fa-trash me-2"></i>
                                Delete Note
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Referral Modal */}
            <Modal show={showReferralModal} onHide={() => setShowReferralModal(false)} size="lg" centered>
                <Modal.Header closeButton className="bg-primary text-white">
                    <Modal.Title>
                        <i className="fas fa-handshake me-2"></i>
                        {referralType === 'referrals' ? 'User Referrals Made' : 'User Referred By'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-0">
                    <Table striped hover responsive className="mb-0">
                        <thead className="table-light">
                            <tr>
                                <th>{referralType === 'referrals' ? 'Referee' : 'Referrer'}</th>
                                <th>Referral ID</th>
                                <th>Status</th>
                                <th>Reward</th>
                                <th>Created</th>
                            </tr>
                        </thead>
                        <tbody>
                            {referralType === 'referrals' ? (
                                props.userReferrals && props.userReferrals.length > 0 ? (
                                    props.userReferrals.map((referral) => (
                                        <tr key={referral._id}>
                                            <td>
                                                <div>
                                                    <div className="fw-bold">{referral.referee?.name || 'N/A'}</div>
                                                    <small className="text-muted">{referral.referee?.phone || 'N/A'}</small>
                                                </div>
                                            </td>
                                            <td>
                                                <code>{referral.referralId}</code>
                                            </td>
                                            <td>
                                                {getStatusBadge(referral.status)}
                                            </td>
                                            <td>
                                                <div>
                                                    <div className="fw-bold">{referral.reward?.totalValue || 0} cans</div>
                                                    <small className="text-muted">
                                                        R: {referral.reward?.referrerCans || 0} | 
                                                        E: {referral.reward?.refereeCans || 0}
                                                    </small>
                                                </div>
                                            </td>
                                            <td>
                                                {formatDate(referral.createdAt)}
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="5" className="text-center py-4">
                                            <i className="fas fa-handshake fa-3x text-muted mb-3"></i>
                                            <p className="text-muted">No referrals made by this user.</p>
                                        </td>
                                    </tr>
                                )
                            ) : (
                                props.userReferredBy && props.userReferredBy.length > 0 ? (
                                    props.userReferredBy.map((referral) => (
                                        <tr key={referral._id}>
                                            <td>
                                                <div>
                                                    <div className="fw-bold">{referral.referrer?.name || 'N/A'}</div>
                                                    <small className="text-muted">{referral.referrer?.phone || 'N/A'}</small>
                                                </div>
                                            </td>
                                            <td>
                                                <code>{referral.referralId}</code>
                                            </td>
                                            <td>
                                                {getStatusBadge(referral.status)}
                                            </td>
                                            <td>
                                                <div>
                                                    <div className="fw-bold">{referral.reward?.totalValue || 0} cans</div>
                                                    <small className="text-muted">
                                                        R: {referral.reward?.referrerCans || 0} | 
                                                        E: {referral.reward?.refereeCans || 0}
                                                    </small>
                                                </div>
                                            </td>
                                            <td>
                                                {formatDate(referral.createdAt)}
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="5" className="text-center py-4">
                                            <i className="fas fa-user-plus fa-3x text-muted mb-3"></i>
                                            <p className="text-muted">This user was not referred by anyone.</p>
                                        </td>
                                    </tr>
                                )
                            )}
                        </tbody>
                    </Table>
                </Modal.Body>
                <Modal.Footer>
                    <Button 
                        variant="outline-secondary" 
                        onClick={() => setShowReferralModal(false)}
                    >
                        <i className="fas fa-times me-2"></i>
                        Close
                    </Button>
                    <Button 
                        as={Link}
                        to={`/inventory/v1.2/referral-ledger?${referralType === 'referrals' ? 'referrer' : 'referee'}=${props.user ? props.user._id : ''}`}
                        variant="primary"
                        target="_blank"
                    >
                        <i className="fas fa-external-link-alt me-2"></i>
                        View Full Referral Ledger
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Custom CSS for Quick Action buttons */}
            <style>{`
                .quick-action-btn:hover {
                    background-color: #000 !important;
                    border-color: #000 !important;
                    color: #fff !important;
                }
                .quick-action-btn:hover i {
                    color: #fff !important;
                }
            `}</style>
        </Fragment>
    );
}

User.propTypes = {
    getUser: PropTypes.func.isRequired,
    addUser: PropTypes.func.isRequired,
    updateUser: PropTypes.func.isRequired,
    updateDeposit: PropTypes.func.isRequired,
    updateCans: PropTypes.func.isRequired,
    getAllNotes: PropTypes.func.isRequired,
    createNote: PropTypes.func.isRequired,
    updateNote: PropTypes.func.isRequired,
    deleteNote: PropTypes.func.isRequired,
    getUserReferrals: PropTypes.func.isRequired,
    getUserReferredBy: PropTypes.func.isRequired,
    loading: PropTypes.bool.isRequired,
    userReferrals: PropTypes.array,
    userReferredBy: PropTypes.array,
    referral: PropTypes.object,
    canReturns: PropTypes.array
};

const mapStateToProps = state => ({
    loading: state.user.loading,
    user: state.user.user,
    deposit: state.user.deposit,
    cans: state.user.cans,
    notes: state.notes,
    userReferrals: state.referralLedger.userReferrals,
    userReferredBy: state.referralLedger.userReferredBy,
    referral: state.user.referral,
    canReturns: state.user.canReturns
});

export default connect( mapStateToProps, { addUser, updateUser, getUser,
    updateDeposit, updateCans, getAllNotes, createNote, updateNote, deleteNote,
    getUserReferrals, getUserReferredBy }) ( User );
